import { useState, useEffect, useCallback } from 'react'
import { motion, AnimatePresence } from 'motion/react'
import { Bug } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import BottomNav from './components/BottomNav'
import SplashScreen from './components/SplashScreen'
import { ChatScreen as Chat } from './features/chat'
import { BriefingScreen as Briefing } from './features/briefing'
import { SettingsScreen as Settings } from './features/settings'
import { NotebookScreen as Notebook } from './features/notebook'
import { CortexScreen as Cortex } from './features/cortex'
import { ActivityScreen as Activity } from './features/activity'
import { MailScreen as Mail } from './features/mail'
import { EmailScreen as EmailSettings } from './features/email-settings'
import { CalendarScreen as CalendarSettings } from './features/calendar-settings'
import { FollowupsScreen as Followups } from './features/followups'
import { Login, SetPassword, NotInvitedScreen, InviteCheckErrorScreen } from './features/auth'
import { Onboarding, getOnboardingStatus } from './features/onboarding'
import { AdminScreen, getInviteStatus } from './features/admin'
import { invalidateCache } from './shared/api/base'
import { PluginsScreen as Plugins, PluginProvider } from './features/plugins'
import { AuthProvider, useAuth } from './features/auth'
import { DebugProvider, useDebugData, DebugModal } from './features/debug'
import { useLabs } from './hooks/useLabs'
import { syncLanguageFromServer } from './i18n/sync'
import { usePushNotifications, usePresenceHeartbeat } from './push'
import { useKeyboardHeight } from './hooks/useKeyboardHeight'
import { slideDirection } from '@tendbot/ui'
import { useSwipeBack } from './shared/hooks/useSwipeBack'
import { useVersionCheck } from './hooks/useVersionCheck'
import UpdateBanner from './components/UpdateBanner'
import type { DeepLink, ScreenName } from './shared/types/navigation'

const TAB_ORDER: ScreenName[] = ['chat', 'mail', 'followups', 'settings']
const SUB_SCREENS = new Set<ScreenName>(['briefing', 'plugins', 'cortex', 'notebook', 'admin', 'activity', 'email', 'calendar'])
const VALID_SCREENS = new Set<ScreenName>(['chat', 'mail', 'notebook', 'settings', 'briefing', 'plugins', 'cortex', 'activity', 'followups', 'email', 'calendar', 'admin'])

function screenFromPath(path: string): ScreenName {
  const segment = path.replace(/^\//, '').split('/')[0] || 'chat'
  return VALID_SCREENS.has(segment as ScreenName) ? (segment as ScreenName) : 'chat'
}

function AppShell() {
  const { user, loading, needsPasswordSet } = useAuth()
  const { i18n } = useTranslation('chat')
  const [activeTab, setActiveTab] = useState<ScreenName>(() => {
    const params = new URLSearchParams(window.location.search)
    if (params.get('connected') === 'google') return 'chat'
    return screenFromPath(window.location.pathname)
  })
  const [deepLink, setDeepLink] = useState<DeepLink | null>(null)
  const [inviteStatus, setInviteStatus] = useState<'approved' | 'pending' | 'rejected' | null>(null)
  const [inviteError, setInviteError] = useState<string | null>(null)
  const [inviteCheckFailed, setInviteCheckFailed] = useState(false)
  const [onboardingComplete, setOnboardingComplete] = useState<boolean | null>(null)
  const [prevTabIndex, setPrevTabIndex] = useState(0)

  // Reset onboarding state when user changes
  const [prevUser, setPrevUser] = useState(user)
  if (user !== prevUser) {
    setPrevUser(user)
    // Clear cached GET responses so we don't serve another account's invite/admin/onboarding status
    invalidateCache()
    setInviteError(null)
    setInviteCheckFailed(false)
    if (!user) {
      setOnboardingComplete(null)
      setInviteStatus(null)
    } else if (!prevUser) {
      // Just logged in — start on chat unless URL specifies a valid screen
      // (e.g. returning from Stripe checkout to /settings)
      const urlScreen = screenFromPath(window.location.pathname)
      if (urlScreen === 'chat') {
        setActiveTab('chat')
        window.history.replaceState({}, '', '/')
      }
    }
  }

  // Check invite + onboarding status. Reusable so the error screen can retry.
  // Promise-chain form (not async/await) keeps setState calls inside .then/.catch
  // closures so the react-hooks/set-state-in-effect rule recognizes them as
  // async continuations rather than synchronous effect-body work.
  const checkInviteStatus = useCallback((): Promise<void> => {
    return getInviteStatus()
      .then(res => {
        const status = res.status as 'approved' | 'pending' | 'rejected'
        setInviteStatus(status)
        if (status === 'approved') {
          return getOnboardingStatus()
            .then(s => { setOnboardingComplete(s.assistantProvisioned) })
            .catch(() => { setOnboardingComplete(false) })
        }
      })
      .catch((err: unknown) => {
        // Surface API/network failures (429, 5xx, offline, etc.) as a real error
        // instead of silently showing the "waiting for approval" screen.
        console.error('[invite-status] check failed', err)
        const msg = err instanceof Error ? err.message : String(err)
        setInviteError(msg)
        setInviteCheckFailed(true)
      })
  }, [])

  const retryInviteCheck = useCallback(async () => {
    setInviteError(null)
    setInviteCheckFailed(false)
    await checkInviteStatus()
  }, [checkInviteStatus])

  useEffect(() => {
    if (!user) return
    checkInviteStatus()
    syncLanguageFromServer()
  }, [user, checkInviteStatus])

  // While the user is waiting for approval, poll every 2s so the app advances
  // automatically the moment an admin approves them — no manual refresh needed.
  useEffect(() => {
    if (!user) return
    if (inviteCheckFailed) return
    if (inviteStatus === null || inviteStatus === 'approved') return
    const id = window.setInterval(() => { checkInviteStatus() }, 2000)
    return () => window.clearInterval(id)
  }, [user, inviteStatus, inviteCheckFailed, checkInviteStatus])

  // Capture UTM params + referrer on initial load for signup attribution
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const utmData: Record<string, string> = {}
    for (const key of ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'ref']) {
      const val = params.get(key)
      if (val) utmData[key] = val
    }
    if (document.referrer) {
      try {
        const ref = new URL(document.referrer)
        // Only store external referrers (not our own domain)
        if (ref.hostname !== window.location.hostname) {
          utmData.referrer = document.referrer
        }
      } catch { /* invalid referrer URL */ }
    }
    if (Object.keys(utmData).length > 0) {
      // Merge with any existing UTM data (e.g. from a previous page load)
      const existing = sessionStorage.getItem('tendbot-utm')
      const merged = existing ? { ...JSON.parse(existing), ...utmData } : utmData
      sessionStorage.setItem('tendbot-utm', JSON.stringify(merged))
    }
  }, [])

  // Clean up OAuth/Stripe callback URL params (state already set via initial values above)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    if (params.get('connected') === 'google') {
      window.history.replaceState({}, '', '/')
    }
  }, [])

  // Push notifications — register token and handle taps (only after auth)
  usePushNotifications(!!user)
  // Presence heartbeat — suppresses push when user is actively online
  usePresenceHeartbeat(!!user)

  const navigateTo = useCallback((tab: string, params: DeepLink | null = null) => {
    setPrevTabIndex(TAB_ORDER.indexOf(SUB_SCREENS.has(activeTab) ? 'settings' : activeTab))
    setDeepLink(params)
    setActiveTab(tab as ScreenName)
    const url = tab === 'chat' ? '/' : `/${tab}`
    if (window.location.pathname !== url) {
      window.history.pushState({ screen: tab }, '', url)
    }
  }, [activeTab])

  useEffect(() => {
    const handler = (e: Event) => {
      const data = (e as CustomEvent).detail ?? {}
      if (data.screen) {
        navigateTo(data.screen as string)
      }
    }
    window.addEventListener('push-navigate', handler)
    return () => window.removeEventListener('push-navigate', handler)
  }, [navigateTo])

  // Sync screen on browser back/forward
  useEffect(() => {
    const onPopState = () => {
      const screen = screenFromPath(window.location.pathname)
      setPrevTabIndex(TAB_ORDER.indexOf(SUB_SCREENS.has(activeTab) ? 'settings' : activeTab))
      setActiveTab(screen)
    }
    window.addEventListener('popstate', onPopState)
    return () => window.removeEventListener('popstate', onPopState)
  }, [activeTab])

  const clearDeepLink = () => setDeepLink(null)

  // Visual viewport tracking for mobile keyboard avoidance
  const { viewportHeight, keyboardOpen } = useKeyboardHeight()

  // Desktop breakpoint for persistent sidebar layout
  const [isDesktop, setIsDesktop] = useState(() => window.matchMedia('(min-width: 1024px)').matches)
  useEffect(() => {
    const mql = window.matchMedia('(min-width: 1024px)')
    const handler = (e: MediaQueryListEvent) => setIsDesktop(e.matches)
    mql.addEventListener('change', handler)
    return () => mql.removeEventListener('change', handler)
  }, [])

  // Swipe-back for sub-screens (must be before early returns — hooks can't be conditional)
  const goBackToSettings = useCallback(() => navigateTo('settings'), [navigateTo])
  const swipeBackProps = useSwipeBack(goBackToSettings)

  // Version check (must be before early returns — hooks can't be conditional)
  const { updateAvailable, upgradeRequired, refresh: refreshApp } = useVersionCheck()

  // Labs / debug (must be before early returns — hooks can't be conditional)
  const labsEnabled = useLabs()
  const debugData = useDebugData()
  const [debugOpen, setDebugOpen] = useState(false)

  const debugButton = labsEnabled && (
    <>
      <button
        onClick={() => setDebugOpen(true)}
        className="fixed z-50 w-8 h-8 rounded-full border border-gold/30 bg-charcoal/80 flex items-center justify-center opacity-50 hover:opacity-100 hover:border-gold hover:shadow-[0_0_12px_rgba(200,149,48,0.2)] transition-all right-3 top-[calc(env(safe-area-inset-top,0px)+12px)] lg:top-auto lg:bottom-4"
      >
        <Bug size={14} className="text-gold" />
      </button>
      <DebugModal
        isOpen={debugOpen}
        onClose={() => setDebugOpen(false)}
        conversationId={debugData.conversationId}
        messages={debugData.messages}
      />
    </>
  )

  // Grain opacity varies by time of day
  const hour = new Date().getHours()
  const grainOpacity = (hour >= 7 && hour <= 19) ? 0.02 : 0.04

  // Shared wrapper for all full-screen states (loading, login, onboarding, etc.)
  // Uses viewportHeight so the keyboard doesn't cover inputs on iOS.
  const fullScreenStyle = { '--grain-opacity': grainOpacity, height: viewportHeight } as React.CSSProperties

  // Loading state
  if (loading) {
    return (
      <div className="grain-overlay fixed inset-x-0 top-0 bg-midnight flex items-center justify-center" style={fullScreenStyle}>
        <SplashScreen />
        {debugButton}
      </div>
    )
  }

  // Not authenticated — show login
  if (!user) {
    return (
      <div className="grain-overlay fixed inset-x-0 top-0 bg-midnight flex flex-col overflow-auto" style={fullScreenStyle}>
        <Login />
        {debugButton}
      </div>
    )
  }

  // Password recovery or invite — show set password
  if (needsPasswordSet) {
    return (
      <div className="grain-overlay fixed inset-x-0 top-0 bg-midnight flex flex-col overflow-auto" style={fullScreenStyle}>
        <SetPassword />
        {debugButton}
      </div>
    )
  }

  // Invite-status check failed (429, 5xx, offline, etc.) — show error + retry
  if (inviteCheckFailed) {
    return (
      <div className="grain-overlay fixed inset-x-0 top-0 bg-midnight flex flex-col overflow-auto" style={fullScreenStyle}>
        <InviteCheckErrorScreen errorMessage={inviteError ?? ''} onRetry={retryInviteCheck} />
        {debugButton}
      </div>
    )
  }

  // Checking invite status
  if (inviteStatus === null) {
    return (
      <div className="grain-overlay fixed inset-x-0 top-0 bg-midnight flex items-center justify-center" style={fullScreenStyle}>
        <SplashScreen />
        {debugButton}
      </div>
    )
  }

  // Not approved — show waiting screen
  if (inviteStatus !== 'approved') {
    return (
      <div className="grain-overlay fixed inset-x-0 top-0 bg-midnight flex flex-col overflow-auto" style={fullScreenStyle}>
        <NotInvitedScreen />
        {debugButton}
      </div>
    )
  }

  // Checking onboarding status
  if (onboardingComplete === null) {
    return (
      <div className="grain-overlay fixed inset-x-0 top-0 bg-midnight flex items-center justify-center" style={fullScreenStyle}>
        <SplashScreen />
        {debugButton}
      </div>
    )
  }

  // Onboarding not complete — show wizard
  if (!onboardingComplete) {
    return (
      <div className="grain-overlay fixed inset-x-0 top-0 bg-midnight flex flex-col overflow-auto" style={fullScreenStyle}>
        <Onboarding onComplete={async (lang) => {
          setOnboardingComplete(true)
          await i18n.changeLanguage(lang)
          navigateTo('chat')
          window.history.replaceState({}, '', '/')
        }} />
        {debugButton}
      </div>
    )
  }

  // Authenticated — show app
  const tabIndex = TAB_ORDER.indexOf(SUB_SCREENS.has(activeTab) ? 'settings' : activeTab)
  const direction = SUB_SCREENS.has(activeTab) ? 'up' : (tabIndex > prevTabIndex ? 'right' : 'left')
  const slideProps = slideDirection(direction)

  const renderMobileScreen = () => {
    const isSubScreen = SUB_SCREENS.has(activeTab)
    const wrap = (child: React.ReactNode) => (
      <motion.div key={activeTab} {...slideProps} {...(isSubScreen ? swipeBackProps : {})} className="h-full">
        {child}
      </motion.div>
    )

    switch (activeTab) {
      case 'mail':
        return wrap(<Mail onNavigate={navigateTo} />)
      case 'briefing':
        return wrap(<Briefing onBack={() => navigateTo('settings')} onNavigate={navigateTo} />)
      case 'settings':
        return wrap(<Settings onNavigate={navigateTo} />)
      case 'followups':
        return wrap(<Followups />)
      case 'notebook':
        return wrap(<Notebook deepLink={deepLink} onDeepLinkConsumed={clearDeepLink} onBack={() => navigateTo('settings')} />)
      case 'plugins':
        return wrap(<Plugins onBack={() => navigateTo('settings')} />)
      case 'cortex':
        return wrap(<Cortex onBack={() => navigateTo('settings')} />)
      case 'activity':
        return wrap(<Activity onBack={() => navigateTo('settings')} />)
      case 'email':
        return wrap(<EmailSettings onBack={() => navigateTo('settings')} onNavigate={navigateTo} />)
      case 'calendar':
        return wrap(<CalendarSettings onBack={() => navigateTo('settings')} onNavigate={navigateTo} />)
      case 'admin':
        return wrap(<AdminScreen onBack={() => navigateTo('settings')} />)
      default:
        return null
    }
  }

  const renderDesktopContent = () => {
    switch (activeTab) {
      case 'mail':
        return <Mail onNavigate={navigateTo} />
      case 'followups':
        return <Followups />
      case 'notebook':
        return <Notebook deepLink={deepLink} onDeepLinkConsumed={clearDeepLink} onBack={() => navigateTo('settings')} />
      case 'settings':
        return <Settings onNavigate={navigateTo} />
      case 'briefing':
        return <Briefing onBack={() => navigateTo('settings')} onNavigate={navigateTo} />
      case 'plugins':
        return <Plugins onBack={() => navigateTo('settings')} />
      case 'cortex':
        return <Cortex onBack={() => navigateTo('settings')} />
      case 'activity':
        return <Activity onBack={() => navigateTo('settings')} />
      case 'email':
        return <EmailSettings onBack={() => navigateTo('settings')} onNavigate={navigateTo} />
      case 'calendar':
        return <CalendarSettings onBack={() => navigateTo('settings')} onNavigate={navigateTo} />
      case 'admin':
        return <AdminScreen onBack={() => navigateTo('settings')} />
      default:
        return null
    }
  }

  return (
    <PluginProvider>
    <div className="grain-overlay bg-midnight" style={{ '--grain-opacity': grainOpacity } as React.CSSProperties}>
      <div
        className="fixed inset-x-0 top-0 overflow-hidden flex flex-col pt-[env(safe-area-inset-top)]"
        style={{ height: viewportHeight }}
      >
        <UpdateBanner
          updateAvailable={updateAvailable}
          upgradeRequired={upgradeRequired}
          onRefresh={refreshApp}
        />
        <main className="flex-1 overflow-hidden">
          {isDesktop ? (
            <div className="flex h-full">
              <Chat onNavigate={navigateTo} activeTab={activeTab} sidebarOnly={activeTab !== 'chat'} deepLink={activeTab === 'chat' ? deepLink : undefined} onDeepLinkConsumed={clearDeepLink} />
              {activeTab !== 'chat' && (
                <div className="flex-1 overflow-hidden">
                  {renderDesktopContent()}
                </div>
              )}
            </div>
          ) : (
            <>
              {/* Keep Chat always mounted on mobile to preserve conversation state */}
              <div className={activeTab === 'chat' ? 'h-full' : 'hidden'}>
                <Chat onNavigate={navigateTo} deepLink={activeTab === 'chat' ? deepLink : undefined} onDeepLinkConsumed={clearDeepLink} />
              </div>
              <AnimatePresence mode="wait">
                {activeTab !== 'chat' && renderMobileScreen()}
              </AnimatePresence>
            </>
          )}
        </main>

        {!keyboardOpen && (
          <div className="lg:hidden">
            <BottomNav activeTab={activeTab} onNavigate={navigateTo} />
          </div>
        )}
      </div>
    </div>
      {debugButton}
    </PluginProvider>
  )
}

function App() {
  return (
    <AuthProvider>
      <DebugProvider>
        <AppShell />
      </DebugProvider>
    </AuthProvider>
  )
}

export default App
